import * as Utils_String from "VSS/Utils/String";
import Contracts = require("TFS/DistributedTask/Contracts");
import * as StoreCommonBase from "DistributedTaskControls/Common/Stores/Base";
import { ActionsHubManager } from "DistributedTaskControls/Common/Actions/ActionsHubManager";
import Model = require("DistributedTaskControls/SharedControls/SharedProjectsPanel/SharedProjectsPanelModel");
import { SharedProjectsPanelActions } from "DistributedTaskControls/SharedControls/SharedProjectsPanel/SharedProjectsPanelActions";

export class SharedProjectsPanelStore extends StoreCommonBase.StoreBase {

    public static getKey(): string {
        return this._sharedProjectPanelStoreKey;
    }

    public initialize(): void {
        super.initialize();
        this._sharedProjectsPanelActions = ActionsHubManager.GetActionsHub<SharedProjectsPanelActions>(SharedProjectsPanelActions);
        this._sharedProjectsPanelActions.sharedProjectsData.addListener(this.loadSharedProjectsData, this);
        this._sharedProjectsPanelActions.updateSharedProjects.addListener(this.updateSharedProjects, this);
        this._sharedProjectsPanelActions.removedSharedProject.addListener(this.removeSharedProject, this);
        this._sharedProjectsPanelActions.addedSharedProject.addListener(this.addSharedProject, this);
        this._sharedProjectsPanelActions.updateError.addListener(this.updateError, this);
        this._sharedProjectsPanelActions.handlePanelClose.addListener(this.handlePanelClose, this);
    }

    protected disposeInternal(): void {
        this._sharedProjectsPanelActions.sharedProjectsData.removeListener(this.loadSharedProjectsData);
        this._sharedProjectsPanelActions.updateSharedProjects.removeListener(this.updateSharedProjects);
        this._sharedProjectsPanelActions.removedSharedProject.removeListener(this.removeSharedProject);
        this._sharedProjectsPanelActions.addedSharedProject.removeListener(this.addSharedProject);
        this._sharedProjectsPanelActions.updateError.removeListener(this.updateError);
        this._sharedProjectsPanelActions.handlePanelClose.removeListener(this.handlePanelClose);
    }

    public loadSharedProjectsData(sharedProjectsData: Model.SharedProjectsPanelData) {
        sharedProjectsData.allProjects = this._arrangeAllProjectList(sharedProjectsData.allProjects, sharedProjectsData.sharedProjects);
        this._originalData = sharedProjectsData;
        this._updatedData = Object.assign({}, sharedProjectsData);
        this.emitChanged();
    }

    public updateSharedProjects(projects: Contracts.ProjectReference[]) {
        this._updatedData.sharedProjects = projects;
        this.emitChanged();
    }

    public removeSharedProject(project: Contracts.ProjectReference) {
        for (let i = 0; i < this._originalData.sharedProjects.length; i++) {
            if (this._originalData.sharedProjects[i].name === project.name) {
                this._originalData.sharedProjects.splice(i, 1);
                break;
            }
        }

        this._isUpdated = true;
        this.emitChanged();
    }

    public addSharedProject(project: Contracts.ProjectReference) {
        this._originalData.sharedProjects.push(project);
        this._isUpdated = true;
        this.emitChanged();
    }

    public updateError(errorMessage: string) {
        if (errorMessage !== Utils_String.empty) {
            this._errorMessage = Utils_String.localeFormat("{0}\n{1}", this._errorMessage, errorMessage);
        } else {
            this._errorMessage = "";
        }

        this.emitChanged();
    }

    public handlePanelClose(): void {
        this._isUpdated = false;
    }

    public getSharedProjectsData(): Model.SharedProjectsPanelData {
        return this._updatedData;
    }

    public getOriginallySharedProjects(): Contracts.ProjectReference[] {
        return this._originalData.sharedProjects;
    }

    public getErrorMessage(): string {
        return this._errorMessage;
    }

    public getNewSharedProjects(): Contracts.ProjectReference[] {
        let newSharedProjects: Contracts.ProjectReference[] = [];

        this._updatedData.sharedProjects.forEach(project => {
            if (!this._originalData.sharedProjects.find(prj => prj.name === project.name)) {
                newSharedProjects.push(project);
            }
        });

        return newSharedProjects;
    }

    public getDeletedSharedProjects(): Contracts.ProjectReference[] {
        let deletedSharedProjects: Contracts.ProjectReference[] = [];

        this._originalData.sharedProjects.forEach(project => {
            if (!this._updatedData.sharedProjects.find(prj => prj.name === project.name)) {
                deletedSharedProjects.push(project);
            }
        });

        return deletedSharedProjects;
    }


    public isDirty(): boolean {
        if (!this._originalData || !this._updatedData) {
            return false;
        }

        if (this._originalData.sharedProjects.length !== this._updatedData.sharedProjects.length) {
            return true;
        }

        this._originalData.sharedProjects.sort(this._projectReferenceComparer);
        this._updatedData.sharedProjects.sort(this._projectReferenceComparer);

        for (let i = 0; i < this._originalData.sharedProjects.length; i++) {
            if (this._originalData.sharedProjects[i].id !== this._updatedData.sharedProjects[i].id) {
                return true;
            }
        }

        return false;
    }

    public isShared(): boolean {
        return (this._originalData.sharedProjects.length > 0);
    }

    public isUpdated(): boolean {
        return this._isUpdated;
    }

    private _arrangeAllProjectList(allProjects: Contracts.ProjectReference[], sharedProjects: Contracts.ProjectReference[]): Contracts.ProjectReference[] {
        allProjects.sort(this._projectReferenceComparer);
        sharedProjects.sort(this._projectReferenceComparer);

        let arrangedAllProjects: Contracts.ProjectReference[] = [];
        let sharedProjectsMap = [];
        sharedProjects.forEach(sharedProject => {
            arrangedAllProjects.push(sharedProject);
            sharedProjectsMap[sharedProject.name] = true;
        });

        allProjects.forEach(project => {
            if (!sharedProjectsMap[project.name]) {
                arrangedAllProjects.push(project);
            }
        });

        return arrangedAllProjects;
    }

    private _projectReferenceComparer(project1: Contracts.ProjectReference, project2: Contracts.ProjectReference): number {
        let x = project1.name.toLowerCase();
        let y = project2.name.toLowerCase();
        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    }

    private _originalData: Model.SharedProjectsPanelData;
    private _updatedData: Model.SharedProjectsPanelData;
    private _errorMessage: string = "";
    private _isUpdated: boolean = false;
    private _sharedProjectsPanelActions: SharedProjectsPanelActions;
    private static _sharedProjectPanelStoreKey: string = "SharedProjectPanelStoreKey";
}